import { Component, OnInit } from '@angular/core';
import {
  PaymentMethod,
  PaymentRequestDto,
} from '@app/model/payment-request/payment-request.dto';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { CreditService } from '@app/service/credit/credit.service';
import { LoggingService } from '@app/service/logging-service/logging.service';
import {
  CheckoutType,
  RedirectService,
} from '@app/service/redirect-service/redirect.service';
import { WaitThen } from '@app/service/state/state.service';
import { environment } from 'src/environments/environment';
import { CurrencyCode } from '@app/model/currency';
import { RecordingService } from '@app/service/recording-service/recording.service';

@Component({
  selector: 'app-success',
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.css', '../page-shared.css'],
})
export class SuccessComponent implements OnInit {
  paymentRequest: PaymentRequestDto;
  amount: number;
  currencyCode: CurrencyCode;

  paymentMethod: string;
  settlementMessage: string;
  title: string;
  noResponseMessage: string;
  autoClosingMessage: string;

  successUrl: string;
  waitingForRedirect: boolean = true;
  showContent: boolean = false;
  loading: boolean = true;
  /** Prevent contents to "flash" if redirect happens quickly */
  private antiFlashTimer: number;

  public readonly APP_STORE_LINK =
    'https://apps.apple.com/se/app/zaver-handla-på-dina-villkor/id1560858500';
  public readonly PLAY_STORE_LINK =
    'https://play.google.com/store/apps/details?id=io.zaver.app';

  constructor(
    private creditService: CreditService,
    private queryParamsService: QueryParamsService,
    private loggingService: LoggingService,
    private redirectService: RedirectService,
    private recordingService: RecordingService
  ) {}

  ngOnInit() {
    const paymentRequestId: string =
      this.queryParamsService.getQueryParams().id;
    this.loggingService.warn('loaded success page!');

    this.creditService
      .getPaymentRequestPublicInfo(paymentRequestId)
      .subscribe((res) => {
        this.paymentRequest = res;
        this.setupPaymentMethod(res.settlementMethod);
        this.amount = this.paymentRequest.amount;
        this.currencyCode = this.paymentRequest.currency;

        if (this.queryParamsService.getQueryParams().child) {
          this.autoClosingMessage = `Återvänder till ${res.merchantDetails.companyName}`;
          setTimeout(() => {
            window.close();
            // Close window after 2 seconds
          }, 2000) as unknown as number;

          return;
        } else {
          this.loading = false;
        }
      });
    this.antiFlashTimer = setTimeout(() => {
      this.showContent = true;
    }, 500) as unknown as number;

    // Wait a short time before saving events.
    setTimeout(() => {
      this.recordingService.saveSuccessRecording();
    }, 800) as unknown as number;

    this.tryRedirectToSuccessUrl();
  }

  public openTab(url: string): void {
    window.open(url);
  }

  public navigateToMerchant() {
    if (this.successUrl) {
      window.location.href = this.successUrl;
    }
  }

  setupPaymentMethod(type: PaymentMethod) {
    switch (type) {
      case PaymentMethod.TRUSTLY:
        this.paymentMethod = 'Banköverföring';
        this.title = 'Tack för din betalning!';
        this.settlementMessage =
          'Pengarna har dragits från ditt valda bankkonto.';
        break;
      case PaymentMethod.INVOICE:
        this.paymentMethod = 'Faktura';
        this.title = 'Tack för att du betalar med Zaver.';
        this.settlementMessage =
          'Innan det är dags att betala kommer din faktura dyka upp ' +
          'i Zaver-appen och skickas till din angivna mejladress.';
        break;
      case PaymentMethod.CREDIT:
      case PaymentMethod.CREDIT_STATIC_AMOUNT:
        this.paymentMethod = 'Delbetalning';
        this.title = 'Tack för att du betalar med Zaver.';
        this.settlementMessage =
          'Innan det är dags att betala kommer dina fakturor dyka upp ' +
          'i Zaver-appen och skickas till din angivna mejladress.';
        break;
      case PaymentMethod.SWISH_EXTERNAL:
        this.paymentMethod = 'Swish';
        this.title = 'Tack för din betalning!';
        this.settlementMessage =
          'Ladda ned Zaver-appen och få full överblick över dina transaktioner.';
        break;
      case PaymentMethod.VIPPS_EXTERNAL:
        this.paymentMethod = 'Vipps';
        this.title = 'Takk for betalingen';
        this.settlementMessage =
          'Last ned Zaver-appen og få full oversikt over transaksjonene dine.'; //TODO(Markus): Hack!! Find why mobile goes to swedish success page..
        break;
      case PaymentMethod.INSTANT_DEBIT:
        this.paymentMethod = 'Betala nu';
        this.title = 'Tack för att du betalar med Zaver.';
        this.settlementMessage =
          'Pengarna dras från det valda kontot inom 1-3 bankdagar.';
        break;
    }
  }

  private async tryRedirectToSuccessUrl() {
    (
      await this.redirectService.tryRedirect(CheckoutType.CLASSIC_CHECKOUT)
    ).subscribe((stayOrLeave) => {
      switch (stayOrLeave.then) {
        case WaitThen.WAIT:
          this.showContent = true;
          break;
        case WaitThen.STAY:
          this.waitingForRedirect = false;
          this.successUrl = stayOrLeave.url;
          this.showContent = true;
          break;
        case WaitThen.LEAVE:
          if (stayOrLeave.waitForMs > 1000) {
            this.waitingForRedirect = false;
            this.successUrl = stayOrLeave.url;
            this.showContent = true;
          } else {
            // Stop the timer that shows the content after a short delay since we're navigating away in less than a second.
            clearTimeout(this.antiFlashTimer);
            // In case the merchant server had a hick-up, show the page and navigation-button after a while.
            setTimeout(() => {
              if (!environment.production) {
                this.noResponseMessage = 'No response from callbackUrl';
              }

              this.waitingForRedirect = false;
              this.successUrl = stayOrLeave.url;
              this.showContent = true;
            }, 4000);
          }
          break;
      }
    });
  }
}
